import React, { useEffect, useState } from "react";
import FeatherIcon from "feather-icons-react";
import { useNavigate } from "react-router-dom";
import Table from "../../components/datatable/datatable";
import { fetchDashboardData, adminFetchAllApplications } from "../../core/axios/endpoints";
import { RoutesFullPath } from "../../utils/constants";

// Sinds de subsidie-partnerflow kan een partner-factuur ook EIA/ISDE-regels
// bevatten. De server splitst het betaalde netto-bedrag per module
// (partnerInvoicePaidNetBtw / partnerInvoicePaidNetSubsidie). Ontbreken die
// velden (oudere server), dan telt alles zoals voorheen bij BTW.
const splitPartnerPaid = (d) => {
  const total = d?.partnerInvoicePaidNet ?? 0;
  const hasSplit =
    d?.partnerInvoicePaidNetBtw != null || d?.partnerInvoicePaidNetSubsidie != null;
  if (!hasSplit) return { btw: total, subsidie: 0 };
  return {
    btw: d?.partnerInvoicePaidNetBtw ?? 0,
    subsidie: d?.partnerInvoicePaidNetSubsidie ?? 0,
  };
};

const AdminDashboardPage = () => {
  const [data, setData] = useState({ data: [], totalDataCount: 0 });
  const [dashboardData, setDashboardData] = useState({
    totalApplications: 0,
    applicationCounts: {
      eia: 0,
      eiaZon: 0,
      isde: 0,
    },
    totalPaidInvoices: 0,
    btwRevenuePaid: 0,
    btwPaidCount: 0,
    btwAssignedToMe: 0,
    btwUnassigned: 0,
    btwStuck: 0,
    partnerInvoicePaidNet: 0,
    partnerInvoicePaidNetBtw: null,
    partnerInvoicePaidNetSubsidie: null,
    partnerInvoiceOpen: 0,
    partnerInvoiceCount: 0,
    partnerCreditNoteCount: 0,
    totalRevenue: 0,
    totalRevenueProjected: 0,
    btwRevenueExpected: 0,
    btwRevenueExpectedCustomer: 0,
    btwRevenueExpectedPartner: 0,
    btwExpectedPartnerCount: 0,
    latestApplications: [],
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();


  const formatNumber = (number) => {
    return number.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };


  const columns = [
    {
      title: "Naam",
      dataIndex: "naam",
      render: (text, record) => <span>{record?.klantId?.naam}</span>
    },
    {
      title: "Regeling",
      dataIndex: "regling",
      sorter: (a, b) => a.regling.localeCompare(b.regling),
    },
    {
      title: "Datum",
      dataIndex: "createdAt",
      render: (text) => <span>{new Date(text).toLocaleDateString()}</span>,
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Bedrag",
      dataIndex: "totaal",
      render: (text) => <span>€{formatNumber(text || '0')}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
    },
  ];

  const fetchData = async ({ page, pageSize, resetPageSize }) => {
    setLoading(true);
    try {
      const reglings = ["EIA", "EIA Zon", "ISDE"];
      const limitPerRegling = page * pageSize;
      const responses = await Promise.all(
        reglings.map((regling) =>
          adminFetchAllApplications(1, limitPerRegling, "", regling, "new")
        )
      );

      const merged = responses
        .flatMap((r) => r.docs || [])
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      const start = (page - 1) * pageSize;
      const end = start + pageSize;
      const paged = merged.slice(start, end).map((item, index) => ({ ...item, key: start + index }));

      const totalDataCount = responses.reduce((sum, r) => sum + (r.totalDocs || 0), 0);

      const result = {
        data: paged,
        totalDataCount,
        resetPageSize,
      };
      setData(result);
    } catch (error) {
      console.error("Error fetching applications for dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCardsData = async () => {
    try {
      const response = await fetchDashboardData();
      setDashboardData(response);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  const handleViewClick = (record) => {
    navigate(`${RoutesFullPath.aanvraagDetailsEIAAdmin}/${record?._id}`, {
        state: {admin:true}
    });
  };



  useEffect(() => {
    fetchCardsData();
  }, []);

  const fmt = (n) =>
    (n ?? 0).toLocaleString("nl-NL", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const totalAanvragen =
    (dashboardData.totalApplications ?? 0) + (dashboardData.btwPaidCount ?? 0);
  const partnerPaid = splitPartnerPaid(dashboardData);

  return (
    <div className="page-wrapper">
      <div className="content admin-pages px-8 pt-6 pb-12">
        <header className="mb-6">
          <nav className="mb-1 text-xs text-ink-subtlest" aria-label="Breadcrumb">
            <span>Dashboard</span>
          </nav>
          <h1 className="text-2xl font-medium text-ink">Dashboard</h1>
          <p className="mt-1 text-sm text-ink-subtlest">
            Overzicht van omzet, aanvragen en openstaande acties.
          </p>
        </header>

        {/* KPI-rij */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded bg-white p-4 shadow-card">
            <span className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded bg-[#DCFFF1] text-[#1F845A]"><FeatherIcon icon="trending-up" size={16} /></span>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
              Totaal omzet
            </p>
            <p className="mt-2 text-2xl font-medium tabular-nums text-ink">
              € {fmt(dashboardData.totalRevenue)}
            </p>
            <p className="mt-1 text-xs text-ink-subtlest">
              Subsidie + BTW betaald
            </p>
            {dashboardData.btwRevenueExpected ? (
              <p className="mt-1 text-xs font-medium text-[#7F5F01]">
                + € {fmt(dashboardData.btwRevenueExpected)} verwacht = € {fmt(dashboardData.totalRevenueProjected)} projectie
              </p>
            ) : null}
          </div>

          <div className="rounded bg-white p-4 shadow-card">
            <span className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded bg-brand-subtle text-brand"><FeatherIcon icon="zap" size={16} /></span>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
              Subsidie-omzet
            </p>
            <p className="mt-2 text-2xl font-medium tabular-nums text-ink">
              € {fmt((dashboardData.totalPaidInvoices ?? 0) + partnerPaid.subsidie)}
            </p>
            <p className="mt-1 text-xs text-ink-subtlest">
              EIA · EIA Zon · ISDE
              {partnerPaid.subsidie ? ` · waarvan € ${fmt(partnerPaid.subsidie)} via partners` : ""}
            </p>
          </div>

          <div className="rounded bg-white p-4 shadow-card">
            <span className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded bg-[#F3F0FF] text-[#5E4DB2]"><FeatherIcon icon="battery-charging" size={16} /></span>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
              BTW-teruggave omzet
            </p>
            <p className="mt-2 text-2xl font-medium tabular-nums text-ink">
              € {fmt((dashboardData.btwRevenuePaid ?? 0) + partnerPaid.btw)}
            </p>
            <p className="mt-1 text-xs text-ink-subtlest">
              {dashboardData.btwPaidCount ?? 0} aanvragen betaald
            </p>
            {dashboardData.btwRevenueExpected ? (
              <p className="mt-1 text-xs font-medium text-[#7F5F01]">
                € {fmt(dashboardData.btwRevenueExpected)} verwacht (nog niet betaald)
              </p>
            ) : null}
          </div>

          <div className="rounded bg-white p-4 shadow-card">
            <span className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded bg-[#FFF7D6] text-[#7F5F01]"><FeatherIcon icon="layers" size={16} /></span>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
              Totaal aanvragen
            </p>
            <p className="mt-2 text-2xl font-medium tabular-nums text-ink">
              {totalAanvragen}
            </p>
            <p className="mt-1 text-xs text-ink-subtlest">
              {dashboardData.totalApplications ?? 0} subsidie ·{" "}
              {dashboardData.btwPaidCount ?? 0} BTW
            </p>
          </div>
        </div>

        {/* Actierij: BTW workflow */}
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
            Te doen - BTW workflow
          </h2>
          <button
            type="button"
            onClick={() => navigate("/panel/admin/btw-batterij-board")}
            className="text-xs font-medium text-brand hover:underline"
          >
            Open board →
          </button>
        </div>
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[
            {
              label: "Toegewezen aan mij",
              value: dashboardData.btwAssignedToMe ?? 0,
              to: "/panel/admin/btw-batterij-board?mine=1",
              dot: "bg-brand",
            },
            {
              label: "Niet toegewezen",
              value: dashboardData.btwUnassigned ?? 0,
              to: "/panel/admin/btw-batterij-board?unassigned=1",
              dot: "bg-[#7F5F01]",
            },
            {
              label: "Vastgelopen (>7d)",
              value: dashboardData.btwStuck ?? 0,
              to: "/panel/admin/btw-batterij-board?stuck=1",
              dot: "bg-[#C9372C]",
            },
          ].map((card) => (
            <button
              key={card.label}
              type="button"
              onClick={() => navigate(card.to)}
              className="group flex items-center justify-between rounded bg-white p-4 text-left shadow-card transition hover:bg-sunken"
            >
              <div>
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${card.dot}`} />
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
                    {card.label}
                  </span>
                </div>
                <p
                  className={`mt-2 text-2xl font-medium tabular-nums ${
                    card.value > 0 ? "text-ink" : "text-ink-disabled"
                  }`}
                >
                  {card.value}
                </p>
              </div>
              <svg
                className="h-4 w-4 text-ink-disabled transition group-hover:translate-x-0.5 group-hover:text-ink-subtle"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M7.3 4.3a1 1 0 011.4 0l5 5a1 1 0 010 1.4l-5 5a1 1 0 11-1.4-1.4L11.6 10 7.3 5.7a1 1 0 010-1.4z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          ))}
        </div>

        {/* Uitsplitsingen */}
        <div className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded bg-white p-4 shadow-card">
            <div className="flex items-baseline justify-between border-b border-line pb-3">
              <h3 className="text-base font-semibold text-ink">
                Subsidie-aanvragen
              </h3>
              <span className="text-xl font-medium tabular-nums text-ink">
                {dashboardData.totalApplications ?? 0}
              </span>
            </div>
            <dl className="mt-3 space-y-2">
              {[
                { label: "EIA", value: dashboardData.applicationCounts?.eia ?? 0 },
                { label: "EIA Zon", value: dashboardData.applicationCounts?.eiaZon ?? 0 },
                { label: "ISDE", value: dashboardData.applicationCounts?.isde ?? 0 },
              ].map((it) => (
                <div
                  key={it.label}
                  className="flex items-center justify-between text-sm"
                >
                  <dt className="text-ink-subtlest">{it.label}</dt>
                  <dd className="font-medium tabular-nums text-ink">
                    {it.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="rounded bg-white p-4 shadow-card">
            <div className="flex items-baseline justify-between border-b border-line pb-3">
              <h3 className="text-base font-semibold text-ink">
                BTW-aanvragen
              </h3>
              <span className="text-xl font-medium tabular-nums text-ink">
                {dashboardData.btwPaidCount ?? 0}
              </span>
            </div>
            <dl className="mt-3 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <dt className="text-ink-subtlest">Omzet betaald</dt>
                <dd className="font-medium tabular-nums text-brand">
                  € {fmt((dashboardData.btwRevenuePaid ?? 0) + partnerPaid.btw)}
                </dd>
              </div>
              {dashboardData.btwRevenueExpected ? (
                <div className="flex items-center justify-between text-sm">
                  <dt className="text-ink-subtlest">Verwacht (nog niet betaald)</dt>
                  <dd className="font-medium tabular-nums text-[#7F5F01]">
                    € {fmt(dashboardData.btwRevenueExpected)}
                  </dd>
                </div>
              ) : null}
              {dashboardData.partnerInvoiceOpen ? (
                <div className="flex items-center justify-between text-xs">
                  <dt className="text-ink-subtlest">waarvan al gefactureerd (verstuurd, open)</dt>
                  <dd className="tabular-nums text-ink-subtle">
                    € {fmt(dashboardData.partnerInvoiceOpen)}
                  </dd>
                </div>
              ) : null}
              {dashboardData.partnerCreditNoteCount ? (
                <div className="flex items-center justify-between text-xs">
                  <dt className="text-ink-subtlest">Creditnota&apos;s</dt>
                  <dd className="text-ink-subtle">
                    {dashboardData.partnerCreditNoteCount}
                  </dd>
                </div>
              ) : null}
            </dl>
          </div>
        </div>

        {/* Recente subsidie-aanvragen */}
        <div className="rounded bg-white shadow-card">
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <div>
              <h2 className="text-base font-semibold text-ink">
                Recente subsidie-aanvragen
              </h2>
              <p className="text-xs text-ink-subtlest">
                EIA · EIA Zon · ISDE - nog niet afgerond
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/panel/admin/eia-overzicht")}
              className="text-xs font-medium text-brand hover:underline"
            >
              Alle subsidie-aanvragen →
            </button>
          </div>
          <Table
            columns={columns}
            response={data}
            fetchData={fetchData}
            onRowClick={handleViewClick}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
